import React, { useState, useEffect, useMemo } from 'react';
import Loader from '../../CommanService/Loader';
import apiService from '../../services/apiService';
import {
  calculateSalesSummary,
  processStockStatusSummary,
  processAllInterchangesData,
  filterAndSortData,
  getStatusColor,
  formatCurrency,
  validateSalesData,
  validateStockData
} from './Interchangeutils';

const AllInterchanges = () => {
  // Local state for data
  const [localSalesData, setLocalSalesData] = useState([]);
  const [localStockData, setLocalStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'totalSales', direction: 'desc' });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Individual column filters
  const [columnFilters, setColumnFilters] = useState({
    interchangeNumber: '',
    marStatus: '',
    marSales: '',
    mwrStatus: '',
    mwrSales: '',
    serStatus: '',
    serSales: '',
    swrStatus: '',
          swrSales: '',
      totalSales: ''
    });

  // Part type filter state
  const [selectedPartType, setSelectedPartType] = useState('Engine');
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Starting API fetch for AllInterchanges...');
        setLoading(true);
        setError(null);
        
        // Fetch stock data
       
        const stockResult = await apiService.GetAllStock();
        
        if (stockResult?.data) {
         
          setLocalStockData(stockResult.data);
        } else if (stockResult && Array.isArray(stockResult)) {
          
          setLocalStockData(stockResult);
        } else {
 
          setLocalStockData([]);
        }
        
        // Fetch sales data
        console.log('Fetching sales data...');
        const salesResult = await apiService.GetAllsales();
        console.log('Sales API result:', salesResult);
        if (salesResult?.data) {
          console.log('Setting sales data:', salesResult.data.length, 'records');
          setLocalSalesData(salesResult.data);
        } else if (salesResult && Array.isArray(salesResult)) {
          console.log('Setting sales data (direct array):', salesResult.length, 'records');
          setLocalSalesData(salesResult);
        } else {
          console.warn('No sales data received');
          setLocalSalesData([]);
        }
        
        console.log('API fetch completed');
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError(`Failed to load data: ${error.message}`);
        setLocalStockData([]);
        setLocalSalesData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Validate and clean data
  const salesData = useMemo(() => {
    return validateSalesData(localSalesData || []);
  }, [localSalesData]);

  const stockStatusData = useMemo(() => {
    const result = validateStockData(localStockData || []);
    return result;
  }, [localStockData]);

  // Calculate 90-day sales summary using utility function
  const salesSummary = useMemo(() => {
    return calculateSalesSummary(salesData);
  }, [salesData]);

  // Process stock status summary using utility function - pass selectedPartType
  const stockStatusSummary = useMemo(() => {
    const result = processStockStatusSummary(stockStatusData, selectedPartType);
    return result;
  }, [stockStatusData, selectedPartType]);

  // Process all interchanges data using utility function - pass selectedPartType
  const allInterchangesData = useMemo(() => {
    const result = processAllInterchangesData(stockStatusData, salesSummary, selectedPartType);
    return result;
  }, [stockStatusData, salesSummary, selectedPartType]);

  // REMOVED: Duplicate filtering logic - utility functions already handle part type filtering

  // Enhanced filtering function with column-specific filters
  const applyFilters = (data) => {
    return data.filter(row => {
      // Global filter
      if (filterText) {
        const globalMatch = Object.values(row).some(value => {
          const stringValue = value === null || value === undefined ? 'NA' : String(value);
          return stringValue.toLowerCase().includes(filterText.toLowerCase());
        });
        if (!globalMatch) return false;
      }

      // Column-specific filters
      if (columnFilters.interchangeNumber && 
          !String(row.interchangeNumber || '').toLowerCase().includes(columnFilters.interchangeNumber.toLowerCase())) {
        return false;
      }
      
      // Handle status filters - convert null/undefined to 'NA' for filtering
      const getStatusValue = (status) => {
        if (status === null || status === undefined || status === '') {
          return 'NA';
        }
        return String(status);
      };
      
      if (columnFilters.marStatus && 
          !getStatusValue(row.MARStatus).toLowerCase().includes(columnFilters.marStatus.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.marSales && 
          !String(row.MARSales || 0).toLowerCase().includes(columnFilters.marSales.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.mwrStatus && 
          !getStatusValue(row.MWRStatus).toLowerCase().includes(columnFilters.mwrStatus.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.mwrSales && 
          !String(row.MWRSales || 0).toLowerCase().includes(columnFilters.mwrSales.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.serStatus && 
          !getStatusValue(row.SERStatus).toLowerCase().includes(columnFilters.serStatus.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.serSales && 
          !String(row.SERSales || 0).toLowerCase().includes(columnFilters.serSales.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.swrStatus && 
          !getStatusValue(row.SWRStatus).toLowerCase().includes(columnFilters.swrStatus.toLowerCase())) {
        return false;
      }
      
      if (columnFilters.swrSales && 
          !String(row.SWRSales || 0).toLowerCase().includes(columnFilters.swrSales.toLowerCase())) {
        return false;
      }
      
             if (columnFilters.totalSales && 
           !String(row.totalSales || 0).toLowerCase().includes(columnFilters.totalSales.toLowerCase())) {
         return false;
       }

       return true;
      });
  };

  // Apply sorting
  const applySorting = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle numeric values
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string values
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    const filtered = applyFilters(allInterchangesData);
    return applySorting(filtered);
  }, [allInterchangesData, filterText, columnFilters, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, columnFilters, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Handle status badge click
  const handleStatusClick = (region, interchangeNumber) => {
    // This would typically navigate to the main tab with the selected region and interchange
    console.log('Status clicked:', { region, interchangeNumber });
  };

  // Handle global filter change
  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  // Handle column filter change
  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Handle clearing individual column filters
  const handleClearColumnFilter = (column) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: ''
    }));
  };

  // Handle clearing global filter
  const handleClearGlobalFilter = () => {
    setFilterText('');
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Render sort indicator
  const renderSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return <span style={{ marginLeft: '5px' }}>
        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
      </span>;
    }
    return null;
  };





  // Render status badge
  const renderStatusBadge = (status, region, interchangeNumber) => {
    if (!status) {
      return <span className="interchnage_status_btn status_na">NA</span>;
    }

    return (
      <span 
        className={`interchnage_status_btn ${getStatusColor(status)}`}
        onClick={() => handleStatusClick(region, interchangeNumber)}
        style={{ cursor: 'pointer' }}
        title={`Click to view ${region} - ${interchangeNumber}`}
      >
        {status}
      </span>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="1"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span>Show:</span>
            <select 
              className="form-select mx-2" 
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <span className="pagination-info-text">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
          </span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };

  // Show error if there's an issue
  if (error) {
    return (
      <div className="control_blk_containers filter_blk mb_15">
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
          <button 
            className="btn btn-sm btn-outline-danger ml-3"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loader while loading
  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      {/* Part Type Filter - Top Right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="font_20 fw-bold text_black">All Interchanges</h2>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label htmlFor="partTypeDropdown" style={{ marginRight: 8, fontWeight: 'bold' }}>Part Type:</label>
          <select
            id="partTypeDropdown"
            value={selectedPartType}
            onChange={(e) => {
              console.log('=== PART TYPE CHANGED ===');
              console.log('Previous Part Type:', selectedPartType);
              console.log('New Part Type:', e.target.value);
              console.log('=== END PART TYPE CHANGE ===');
              setSelectedPartType(e.target.value);
            }}
            style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc', fontSize: '14px' }}
          >
            <option value="Engine">Engine</option>
            <option value="Transmission">Transmission</option>
          </select>
        </div>
      </div>

      {/* Stock Status Summary Table */}
      <div className="control_blk_containers filter_blk mb_15 w-50 alingnment">
        <h3 className="font_16 fw-semi text_black mb_15">
          Stock Status Summary by Region
        </h3>
        
        {!stockStatusSummary || stockStatusSummary.length === 0 ? (
          <div className="alert alert-info" role="alert">
            <strong>Info:</strong> No stock data available to display for {selectedPartType}.
            <br />
            <small>Stock data: {stockStatusData.length} records, Sales data: {salesData.length} records</small>
          </div>
        ) : (
          <table className="w-100 table">
            <thead>
              <tr>
                <th>Stock Status</th>
                <th>MAR</th>
                <th>MWR</th>
                <th>SER</th>
                <th>SWR</th>
              </tr>
            </thead>
            <tbody>
              {stockStatusSummary.map((row, index) => (
                <tr key={index}>
                  <td>{row.stockStatus}</td>
                  <td>{row.MAR}</td>
                  <td>{row.MWR}</td>
                  <td>{row.SER}</td>
                  <td>{row.SWR}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* All Interchanges Table */}
      <div className="control_blk_containers filter_blk mb_15">
        <h3 className="font_16 fw-semi text_black mb_15">
          All Interchanges - {selectedPartType}
          <span className="text-muted ml-2">({filteredAndSortedData.length} of {allInterchangesData.length} shown)</span>
        </h3>
        
        {!allInterchangesData || allInterchangesData.length === 0 ? (
          <div className="alert alert-info" role="alert">
            <strong>Info:</strong> No interchange data available to display for {selectedPartType}.
            <br />
            <small>Stock data: {stockStatusData.length} records, Sales data: {salesData.length} records</small>
          </div>
        ) : (
          <>
            {/* Global Filter */}
            <div className="mb-3">
              <div className="global-filter-container">
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Global filter: Search across all columns..."
                  value={filterText}
                  onChange={handleFilterChange}
                />
                {filterText && (
                  <span 
                    className="clear-filter-btn" 
                    onClick={handleClearGlobalFilter}
                    title="Clear global filter"
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="w-100 table table_color">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('interchangeNumber')} style={{ cursor: 'pointer' }}>
                      Interchange{renderSortIndicator('interchangeNumber')}
                    </th>
                    <th>MAR Status</th>
                    <th onClick={() => handleSort('MARSales')} style={{ cursor: 'pointer' }}>
                      MAR Sales{renderSortIndicator('MARSales')}
                    </th>
                    <th>MWR Status</th>
                    <th onClick={() => handleSort('MWRSales')} style={{ cursor: 'pointer' }}>
                      MWR Sales{renderSortIndicator('MWRSales')}
                    </th>
                    <th>SER Status</th>
                    <th onClick={() => handleSort('SERSales')} style={{ cursor: 'pointer' }}>
                      SER Sales{renderSortIndicator('SERSales')}
                    </th>
                    <th>SWR Status</th>
                    <th onClick={() => handleSort('SWRSales')} style={{ cursor: 'pointer' }}>
                      SWR Sales{renderSortIndicator('SWRSales')}
                    </th>
                    <th onClick={() => handleSort('totalSales')} style={{ cursor: 'pointer' }}>
                      Total Sales{renderSortIndicator('totalSales')}
                    </th>
                  </tr>
                  {/* Column Filters Row */}
                  <tr>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter Interchange..."
                        value={columnFilters.interchangeNumber}
                        onChange={(e) => handleColumnFilterChange('interchangeNumber', e.target.value)}
                      />
                      {columnFilters.interchangeNumber && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('interchangeNumber')}
                          title="Clear Interchange filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter MAR Status..."
                        value={columnFilters.marStatus}
                        onChange={(e) => handleColumnFilterChange('marStatus', e.target.value)}
                      />
                      {columnFilters.marStatus && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('marStatus')}
                          title="Clear MAR Status filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter MAR Sales..."
                        value={columnFilters.marSales}
                        onChange={(e) => handleColumnFilterChange('marSales', e.target.value)}
                      />
                      {columnFilters.marSales && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('marSales')}
                          title="Clear MAR Sales filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter MWR Status..."
                        value={columnFilters.mwrStatus}
                        onChange={(e) => handleColumnFilterChange('mwrStatus', e.target.value)}
                      />
                      {columnFilters.mwrStatus && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('mwrStatus')}
                          title="Clear MWR Status filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter MWR Sales..."
                        value={columnFilters.mwrSales}
                        onChange={(e) => handleColumnFilterChange('mwrSales', e.target.value)}
                      />
                      {columnFilters.mwrSales && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('mwrSales')}
                          title="Clear MWR Sales filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter SER Status..."
                        value={columnFilters.serStatus}
                        onChange={(e) => handleColumnFilterChange('serStatus', e.target.value)}
                      />
                      {columnFilters.serStatus && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('serStatus')}
                          title="Clear SER Status filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter SER Sales..."
                        value={columnFilters.serSales}
                        onChange={(e) => handleColumnFilterChange('serSales', e.target.value)}
                      />
                      {columnFilters.serSales && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('serSales')}
                          title="Clear SER Sales filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter SWR Status..."
                        value={columnFilters.swrStatus}
                        onChange={(e) => handleColumnFilterChange('swrStatus', e.target.value)}
                      />
                      {columnFilters.swrStatus && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('swrStatus')}
                          title="Clear SWR Status filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter SWR Sales..."
                        value={columnFilters.swrSales}
                        onChange={(e) => handleColumnFilterChange('swrSales', e.target.value)}
                      />
                      {columnFilters.swrSales && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('swrSales')}
                          title="Clear SWR Sales filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                    <th>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Filter Total Sales..."
                        value={columnFilters.totalSales}
                        onChange={(e) => handleColumnFilterChange('totalSales', e.target.value)}
                      />
                      {columnFilters.totalSales && (
                        <span 
                          className="clear-filter-btn" 
                          onClick={() => handleClearColumnFilter('totalSales')}
                          title="Clear Total Sales filter"
                        >
                          ×
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-4">
                        {filterText || Object.values(columnFilters).some(f => f) 
                          ? 'No interchanges match your filter criteria.' 
                          : 'No interchange data available.'}
                      </td>
                    </tr>
                  ) : (
                    currentData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.interchangeNumber}</td>
                        <td>
                          {renderStatusBadge(row.MARStatus, 'MAR', row.interchangeNumber)}
                        </td>
                        <td>{formatCurrency(row.MARSales)}</td>
                        <td>
                          {renderStatusBadge(row.MWRStatus, 'MWR', row.interchangeNumber)}
                        </td>
                        <td>{formatCurrency(row.MWRSales)}</td>
                        <td>
                          {renderStatusBadge(row.SERStatus, 'SER', row.interchangeNumber)}
                        </td>
                        <td>{formatCurrency(row.SERSales)}</td>
                        <td>
                          {renderStatusBadge(row.SWRStatus, 'SWR', row.interchangeNumber)}
                        </td>
                        <td>{formatCurrency(row.SWRSales)}</td>
                        <td>{formatCurrency(row.totalSales)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  );
};

export default AllInterchanges; 